// DetailPage.js
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../scripts/api";

// --- Status list used by ColorCode dropdown (matches your status.js)
const STATUS_BY_CODE = {
  0: { bg: '#FFFFFF', label: 'UNASSIGNED' },
  1: { bg: '#C757FF', label: 'SOLD' },
  2: { bg: '#FFD966', label: 'RENTED OUT' },
  3: { bg: '#69e7a2ff', label: 'IN STOCK - CALGARY' },
  4: { bg: '#92D050', label: 'IN TRANSIT TO CALGARY' },
  5: { bg: '#92D050', label: 'IN TRANSIT TO CUSTOMER' },
  6: { bg: '#FF0000', label: 'ALERT' },
  7: { bg: '#4183e6ff', label: 'TO ORDER' },
  8: { bg: '#FFCCFF', label: 'ON ORDER' },
  9: { bg: '#00B050', label: 'STOCK & PDI - IN CALGARY' },
  10:{ bg: '#ce951bff', label: 'DEMO' }, 
  11:{ bg: '#1b54ceff', label: 'CHASSIS ALLOCATION SLOT' },
  12:{ bg: '#0a2479ff', label: 'READY AT FACTORY' },
  13: { bg: '#FF0000', label: 'IN TRANSIT - BROKE DOWN' },
};

const COLOR_CODE_OPTIONS = Object.entries(STATUS_BY_CODE)
  .map(([code, v]) => ({ v: String(code), label: `${code} — ${v.label}` }))
  .sort((a, b) => Number(a.v) - Number(b.v));

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer equipment" },
  { v: "sweeper", label: "Sweeper" },
  { v: "refuse", label: "Refuse" },
  { v: "municipal", label: "Municipal" },
  { v: "mrr", label: "MRR" },
  { v: "Rail", label: "Rail" },
  { v: "SnowPlow", label: "Snow Plow"},
];

// columns that are DATE in SQLite
const DATE_FIELDS = new Set([
  "customer_po_date",
  "factory_order_date",
  "chassis_build_date",
  "chassis_arrive_eta",
  "ready_at_factory",
  "arrive_sna",
  "to_customer",
  "body_status_update",
  "chassis_status_update",
]);

// fields that should be treated as numeric/currency TEXT, NOT booleans
const CURRENCY_FIELDS = new Set([
  "exchange",
  "misc_charges",
  "misc_hours",
  "misc_mileage",
  "misc_exchange",
  "misc_addons",
  "misc_repairs",
  "pdi_charges",
  "freight_charges",
  "fright_charges",
  "body_net_sna",
  "body_cost",
  "chassis_net_sna",
  "chassis_cost",
  "total_net_sna",
  "total_cost",
  "total_sell",
  "surcharge",
  "tarrif",
]);

// flags rendered as checkboxes
const CHECKBOX_FIELDS = new Set([
  "is_chassis",
  "is_body",
  "is_attachment",
  "is_equipment",
  "is_stock",
  "is_demo",
  "is_rental",
  "alert",
]);

// GROUPS
const GROUPS = [
  {
    title: "Customer Info",
    fields: [
      { k: "customer", label: "Customer Sold To" },
      { k: "customer_po", label: "Customer PO" },
      { k: "sna_customer_po", label: "SNA Customer PO" },
      { k: "customer_po_date", label: "Customer PO Date" },
      { k: "customer_po_update", label: "Customer PO Update" },
      { k: "sent_so", label: "Sent SO" },
      { k: "sign_so", label: "Sign SO" },
      { k: "notes", label: "Notes" },
    ],
  },
  {
    title: "Unit Info",
    fields: [
      { k: "rowo", label: "RO / WO #" },
      { k: "tft_po", label: "TFT Alco PO"},
      { k: "desc", label: "Description" },
      { k: "stock_po", label: "Stock PO" },
      { k: "factory_production_so_order", label: "Factory Production SO Order" },
      { k: "factory_order_date", label: "Factory Order Date" },
      { k: "factory_wo", label: "Factory WO" },
      { K: "body_make_model", label: "Body Make & Model"},
      { k: "make_model_size", label: "Chassis Model Size" },
      { k: "revd_eng", label: "Revd ENG" },
    ],
  },
  {
    title: "Unit Classification",
    fields: [
      // ✅ move these here, all checkboxes
            // ✅ alert checkbox here too
      { k: "alert", label: "Alert" },
      { k: "is_chassis", label: "Chassis" },
      { k: "is_body", label: "Body" },
      { k: "is_attachment", label: "Attachment" },
      { k: "is_equipment", label: "Equipment" },

      { k: "is_stock", label: "Stock" },
      { k: "is_demo", label: "Demo" },
      { k: "is_rental", label: "Rental" },

      // ✅ move category dropdown here
      { k: "unit_category", label: "Category" },

      // ✅ color_code dropdown here (not SNA Info)
      { k: "color_code", label: "ColorCode" },
      { k: "unit_status", label: "Unit Status" },
    ],
  },
  {
    title: "VIN / Location",
    fields: [
      
      { k: "chassis_make_model", label: "Chassis Make & Model" },
      { k: "type", label: "Unit Type" },
      { k: "chassis_vin", label: "Chassis VIN" },
      { k: "body_vin", label: "Body VIN" },
      { k: "body_chassis_vin", label: "Body/Chassis VIN (combined)" },
      
      { k: "unit_location", label: "Location" },
    ],
  },
  {
    title: "SNA Info",
    fields: [
      { k: "sna_body_chassis_po", label: "SNA Body/Chassis PO" },
      { k: "riv_(TEXT)", label: "RIV (Text)" },
    ],
  },
  {
    title: "Finance",
    fields: [
      { k: "exchange", label: "Exchange" },
      { k: "misc_charges", label: "Misc Charges" },
      { k: "misc_hours", label: "Misc Hours" },
      { k: "misc_mileage", label: "Misc KM" },
      { k: "misc_exchange", label: "Misc Exchange" },
      { k: "misc_addons", label: "Misc Add-ons" },
      { k: "misc_repairs", label: "Misc Repairs" },
      { k: "pdi_charges", label: "PDI" },
      { k: "fright_charges", label: "Freight Cost" },
      { k: "body_net_sna", label: "Body Cost" },
      { k: "chassis_net_sna", label: "Chassis Cost" },
      { k: "surcharge", label: "Surcharge" },
      { k: "tarrif", label: "Tariff" },
      { k: "total_net_sna", label: "Total Cost" },
      { k: "total_sell", label: "Total Sell Price" },
    ],
  },
  {
    title: "Updates (Dates)",
    fields: [
      { k: "chassis_status_update", label: "Chassis Status Update" },
      { k: "body_status_update", label: "Body Status Update" },
      { k: "chassis_arrive_eta", label: "Chassis Arrive ETA" },
      { k: "ready_at_factory", label: "Unit Ready at Factory" },
      { k: "arrive_sna", label: "Unit Arrive Calgary" },
      { k: "chassis_build_date", label: "Chassis Build" },
      { k: "to_customer", label: "To Customer" },
      { k: "factory_pick_up", label: "Factory Pickup (driver)" },
      { k: "factory_del_sna", label: "Delivered to SNA (driver)" },
    ],
  },
];

// Match DB keys even if user typed slightly different (case/spacing/underscores)
function findKey(draft, wantedKey) {
  if (!draft) return null;
  if (Object.prototype.hasOwnProperty.call(draft, wantedKey)) return wantedKey;

  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
  const w = norm(wantedKey);
  for (const k of Object.keys(draft)) {
    if (norm(k) === w) return k;
  }
  return null;
}

function isBoolishValue(v) {
  if (v === 0 || v === 1 || v === true || v === false) return true;
  const s = String(v ?? "").toLowerCase();
  return s === "true" || s === "false" || s === "0" || s === "1" || s === "yes" || s === "no";
}

function toDateInputValue(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return "";
}

function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function getUnitKind(record) {
  const isChassis = isTruthyFlag(record.is_chassis);
  const isBody = isTruthyFlag(record.is_body);
  const isAttachment = isTruthyFlag(record.is_attachment);
  const isEquipment = isTruthyFlag(record.is_equipment);

  if (isChassis || isBody || isAttachment || isEquipment) {
    const parts = [];
    if (isChassis) parts.push("Chassis");
    if (isBody) parts.push("Body");
    if (isAttachment) parts.push("Attachment");
    if (isEquipment) parts.push("Equipment");
    return parts.join(" + ");
  }

  const hasChassis = !!(record.chassis_vin && String(record.chassis_vin).trim());
  const hasBody = !!(record.body_vin && String(record.body_vin).trim());
  if (hasChassis && hasBody) return "Body + Chassis";
  if (hasChassis) return "Chassis";
  if (hasBody) return "Body";
  return "Unit";
}

function Field({ label, fieldKey, value, onChange }) {
  // ✅ ColorCode dropdown from STATUS_BY_CODE
  if (fieldKey === "color_code") {
    const cur = value === null || value === undefined ? "" : String(value).trim();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          {COLOR_CODE_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // ✅ unit_category dropdown
  if (fieldKey === "unit_category") {
    const v = (value ?? "").toString().toLowerCase();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={v} onChange={(e) => onChange(e.target.value)}>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // ✅ date picker
  if (DATE_FIELDS.has(fieldKey)) {
    const raw = value ?? "";
    const v = toDateInputValue(raw);
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <input type="date" className="input" value={v} onChange={(e) => onChange(e.target.value || "")} />
        {raw && !v && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Current (raw): {String(raw)}
          </div>
        )}
      </div>
    );
  }

  // ✅ finance fields always text
  if (CURRENCY_FIELDS.has(fieldKey)) {
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }

  // ✅ all checkboxes (is_* + alert)
  if (CHECKBOX_FIELDS.has(fieldKey)) {
    const checked = isTruthyFlag(value);

    return (
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked ? "1" : "0")} />
        {label}
      </label>
    );
  }

  // generic bool-ish (0/1/true/false/yes/no) — keep as dropdown for other fields
  if (isBoolishValue(value)) {
    const s = String(value ?? "").toLowerCase();
    const cur = s === "1" || s === "true" || s === "yes" ? "1" : s === "0" || s === "false" || s === "no" ? "0" : "";
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          <option value="1">true</option>
          <option value="0">false</option>
        </select>
      </div>
    );
  }

  // default text input
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
      <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SortableGroup({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // ✅ pass handle props down; do NOT attach listeners to wrapper
  const handleProps = { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} style={style}>
      {children(handleProps)}
    </div>
  );
}



function GroupCard({ title, items, draft, setDraft, handleProps }) {
  const isClassification = title === "Unit Classification";

  const wrapperStyle = isClassification
    ? { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      };

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>{title}</div>

        {/* ✅ drag handle only */}
        <button
          type="button"
          {...handleProps}
          style={{
            cursor: "grab",
            userSelect: "none",
            border: "1px solid rgba(0,0,0,0.15)",
            background: "#f5f5f5",
            borderRadius: 8,
            padding: "4px 8px",
            lineHeight: 1,
          }}
          title="Drag to reorder"
          aria-label={`Drag ${title}`}
        >
          ⠿
        </button>
      </div>

      <div style={wrapperStyle}>
        {items.map(({ label, key }) => (
          <Field
            key={key}
            label={label}
            fieldKey={key}
            value={draft?.[key]}
            onChange={(v) => setDraft({ ...draft, [key]: v })}
          />
        ))}
      </div>
    </div>
  );
}


export default function DetailPage() {
  const { table, id } = useParams();
  const nav = useNavigate();

  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [related, setRelated] = useState([]);
  const [relatedErr, setRelatedErr] = useState("");

  // ✅ load record
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/${table}/${id}`);
        if (!alive) return;
        setDraft(data);
      } catch (e) {
        console.error("Load detail failed", e);
        if (alive) setMsg("Failed to load");
      }
    })();
    return () => { alive = false; };
  }, [table, id]);

  // ✅ related records
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setRelatedErr("");
        const { data } = await api.get(`/related/${table}/${id}`);
        if (!alive) return;
        setRelated(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Load related failed", e);
        if (!alive) return;
        setRelated([]);
        setRelatedErr("Failed to load related records");
      }
    })();
    return () => { alive = false; };
  }, [table, id]);

  // ✅ DEFINE THESE FIRST (before orderedItems)
  const resolvedGroups = useMemo(() => {
    if (!draft) return [];
    return GROUPS.map((g) => {
      const items = [];
      for (const f of g.fields) {
        const resolvedKey = findKey(draft, f.k);
        if (!resolvedKey) continue;
        items.push({ label: f.label, key: resolvedKey });
      }
      return { title: g.title, items };
    }).filter((g) => g.items.length > 0);
  }, [draft]);

  const remaining = useMemo(() => {
    if (!draft) return [];
    const used = new Set(["id"]);
    for (const g of resolvedGroups) for (const it of g.items) used.add(it.key);

    return Object.keys(draft)
      .filter((k) => !used.has(k))
      .sort((a, b) => a.localeCompare(b));
  }, [draft, resolvedGroups]);

  // ✅ THEN your group ordering state + orderedItems
  const DEFAULT_GROUP_ORDER = GROUPS.map((g) => g.title).concat(["__remaining__"]);
  const ORDER_KEY = `detail_group_order:${table}`;

  const [groupOrder, setGroupOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return DEFAULT_GROUP_ORDER;
  });

useEffect(() => {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(groupOrder));
  } catch {}
}, [groupOrder, ORDER_KEY]);

const orderedItems = useMemo(() => {
  const byId = new Map();

  // resolvedGroups come from your existing useMemo
  for (const g of resolvedGroups) byId.set(g.title, { type: "group", g });

  // Remaining block
  byId.set("__remaining__", { type: "remaining" });

  // Keep only items that exist (e.g. if a group has 0 fields it won’t be in resolvedGroups)
  const existing = groupOrder.filter((id) => byId.has(id));

  // Append any new groups that exist but aren't in order yet
  for (const [id] of byId) if (!existing.includes(id)) existing.push(id);

  return existing.map((id) => ({ id, ...byId.get(id) }));
}, [groupOrder, resolvedGroups, remaining]);

const sensors = useSensors(
  useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
);

function onDragEnd(event) {
  const { active, over } = event;
  if (!over) return;
  if (active.id === over.id) return;

  setGroupOrder((prev) => {
    const oldIndex = prev.indexOf(active.id);
    const newIndex = prev.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return prev;
    return arrayMove(prev, oldIndex, newIndex);
  });
}

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/${table}/${id}`);
        if (!alive) return;
        setDraft(data);
      } catch (e) {
        console.error("Load detail failed", e);
        if (alive) setMsg("Failed to load");
      }
    })();
    return () => {
      alive = false;
    };
  }, [table, id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setRelatedErr("");
        const { data } = await api.get(`/related/${table}/${id}`);
        if (!alive) return;
        setRelated(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Load related failed", e);
        if (!alive) return;
        setRelated([]);
        setRelatedErr("Failed to load related records");
      }
    })();
    return () => {
      alive = false;
    };
  }, [table, id]);

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const payload = { ...draft };
      delete payload.id;

      // keep alert saved as TEXT "0"/"1"
      if (payload.alert !== undefined) payload.alert = isTruthyFlag(payload.alert) ? "1" : "0";

      const { data: res } = await api.put(`/${table}/${id}`, payload);
      setMsg(`Saved (${res.updated || 0})`);
    } catch (e) {
      console.error("Save failed", e);
      setMsg("Save failed");
    } finally {
      setBusy(false);
    }
  };


  if (!draft) return <div className="container">Loading…</div>;

  const titleKey = draft.rowo || draft.stock_po || draft.id || id;

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 800, letterSpacing: 0.3 }}>Details: {table} #{titleKey}</h2>
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Submit"}
        </button>
      </div>

      {msg && <div className="muted">{msg}</div>}

      <div className="card" style={{ minWidth: 0, marginTop: 12 }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.2, marginBottom: 10 }}>Related Records (same RO/WO or Stock PO)</div>

        {relatedErr && <div className="muted">{relatedErr}</div>}
        {!relatedErr && related.length === 0 && <div className="muted">No related records found.</div>}

        {related.length > 0 && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {related.map((r) => {
              const kind = getUnitKind(r);
              const label = `${kind || "Unit"} • ID #${r.id} • RO/WO ${r.rowo || "-"} • Stock ${r.stock_po || "-"}`;

              return (
                <button key={r.id} className="btn" type="button" onClick={() => nav(`/detail/${table}/${r.id}`)}>
                  Open: {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ IMPORTANT: make cards "fit content" (no equal height stretch) */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
  <SortableContext
  items={orderedItems.map((x) => x.id)}
  strategy={verticalListSortingStrategy}
>
  <div
    style={{
      columnWidth: 360,
      columnGap: 12,
      alignItems: "start",
    }}
  >
    {orderedItems.map((item) => {
      if (item.type === "group") {
        return (
          <SortableGroup key={item.id} id={item.id}>
            {(handleProps) => (
              <div
                style={{
                  breakInside: "avoid",
                  WebkitColumnBreakInside: "avoid",
                  marginBottom: 12,
                  width: "100%",
                  display: "inline-block",
                }}
              >
                <GroupCard
                  title={item.g.title}
                  items={item.g.items}
                  draft={draft}
                  setDraft={setDraft}
                  handleProps={handleProps}
                />
              </div>
            )}
          </SortableGroup>
          );
        }

        // remaining
        return (
          <SortableGroup key={item.id} id={item.id}>
  {(handleProps) => (
    <div className="card" style={{ minWidth: 0 }}>
      <div
        style={{
          breakInside: "avoid",
                WebkitColumnBreakInside: "avoid",
                marginBottom: 12,
                width: "100%",
                display: "inline-block",
        }}
      >
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>Remaining Fields</div>

        <button
          type="button"
          {...handleProps}
          style={{
            cursor: "grab",
            userSelect: "none",
            border: "1px solid rgba(0,0,0,0.15)",
            background: "#f5f5f5",
            borderRadius: 8,
            padding: "4px 8px",
            lineHeight: 1,
          }}
          title="Drag to reorder"
          aria-label="Drag Remaining Fields"
          onMouseDown={(e) => e.preventDefault()}
        >
          ⠿
        </button>
      </div>

      {remaining.length === 0 ? (
        <div className="muted">Nothing extra.</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {remaining.map((k) => (
            <Field
              key={k}
              label={k}
              fieldKey={k}
              value={draft?.[k]}
              onChange={(v) => setDraft({ ...draft, [k]: v })}
            />
          ))}
        </div>
      )}
    </div>
  )}
</SortableGroup>

        );
      })}
    </div>
  </SortableContext>
</DndContext>

    </div>
  );
}